import { useStore } from '@nanostores/react';
import { useMemo, useCallback } from 'react';
import { toast } from 'react-toastify';
import { ActionMenu, type ActionMenuItemData, type ActionMenuType } from '~/components/ui/ActionMenu';
import { teamStore } from '~/lib/stores/team';

import { userStore } from '~/lib/stores/user';
import { type OrganizationData } from '~/types/organization';

enum MemberActionKind {
  Delete = 'delete',
  Resend = 'resend',
  Downgrade = 'downgrade',
  Upgrade = 'upgrade',
}

interface MemberManagementMenuProps {
  organization: OrganizationData;
  usernames?: string[];
  needTierChange?: boolean;
}

export const MemberManagementMenu = ({ organization, usernames, needTierChange }: MemberManagementMenuProps) => {
  const user = useStore(userStore.user);
  const selectedMembers = useStore(teamStore.selectedMembers);
  const members = useStore(teamStore.members);

  const actionableMembers = usernames ?? selectedMembers;

  const canUpgrade = members.some((m) => actionableMembers.includes(m.user.username) && !m.withMembership);

  const canDowngrade = members.some(
    (m) => actionableMembers.includes(m.user.username) && !!m.withMembership && !m.downgraded,
  );

  const handleResendInvites = useCallback(() => {
    if (user?.username && actionableMembers.includes(user.username)) {
      toast.error(`You can't resend an invitation to yourself`);
      return;
    }

    teamStore.resendInvites(actionableMembers, organization);
  }, [actionableMembers, organization, user?.username]);

  const handleDeleteMembers = useCallback(() => {
    if (user?.username && actionableMembers.includes(user.username)) {
      toast.error(`You can't delete yourself from a team`);
      return;
    }

    teamStore.deleteMembers(actionableMembers, organization);
  }, [actionableMembers, organization, user?.username]);

  const handleSubscriptionChange = useCallback(
    (isUpgrade: boolean) => {
      teamStore.updateMembersSubscription(actionableMembers, organization, isUpgrade);
    },
    [actionableMembers, organization],
  );

  const actions = useMemo(() => {
    const arr: ActionMenuItemData[] = [
      {
        id: MemberActionKind.Resend,
        icon: <span className="i-heroicons:envelope text-base" />,
        label: 'Resend invites',
        onAction: handleResendInvites,
      },
    ];

    if (canDowngrade && needTierChange) {
      arr.push({
        id: MemberActionKind.Downgrade,
        icon: <span className="i-heroicons:arrow-down text-base" />,
        label: 'Downgrade to unpaid',
        onAction: () => handleSubscriptionChange(false),
      });
    }

    if (canUpgrade && needTierChange) {
      arr.push({
        id: MemberActionKind.Upgrade,
        icon: <span className="i-heroicons:arrow-up text-base" />,
        label: 'Upgrade to paid',
        onAction: () => handleSubscriptionChange(true),
      });
    }

    if (user?.username && !actionableMembers.includes(user.username)) {
      arr.push({
        id: MemberActionKind.Delete,
        icon: <span className="i-heroicons:user-minus text-base" />,
        type: 'error' as ActionMenuType,
        label: 'Remove from team',
        onAction: handleDeleteMembers,
      });
    }

    return arr;
  }, [
    canDowngrade,
    canUpgrade,
    user?.username,
    actionableMembers,
    handleResendInvites,
    handleDeleteMembers,
    handleSubscriptionChange,
  ]);

  return (
    <ActionMenu
      items={actions}
      triggerNode={<span className="i-heroicons:ellipsis-horizontal-solid text-xl" />}
      triggerClassName="flex gap-2 items-center rounded-md bg-transparent"
    />
  );
};
